// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
/**
 * Apparence des boutons BIO24 — la SEULE définition.
 *
 * Module PUR (aucun import d'exécution, seulement un `import type`) : il est
 * compilé et testé par `npm test` (backend), et il est partagé tel quel par
 * `Bouton` (composant) et par les anciens points d'entrée de `ged`
 * (`btnPrimary`, `btnOutline`, `btnSmall`), qui n'en sont plus que des alias.
 * Changer un bouton, c'est changer CE fichier — nulle part ailleurs.
 *
 * Règles : voir `shared/docs/UI-CONVENTIONS.md`.
 *
 * ⚠️ Jetons `var(--…)` UNIQUEMENT, et uniquement des jetons présents dans les
 * trois apps (`ged`, `manuel`, `site`). `--red-200`, par exemple, n'existe pas
 * dans `manuel` ni `site` : s'en servir ici ferait un bouton sans bordure
 * dans deux apps sur trois, sans la moindre erreur.
 */
import type { CSSProperties } from 'react';

export type VarianteBouton = 'primaire' | 'secondaire' | 'contour' | 'discret' | 'danger';
export type TailleBouton = 'md' | 'sm';

export const VARIANTES_BOUTON: readonly VarianteBouton[] = ['primaire', 'secondaire', 'contour', 'discret', 'danger'];

/** Hauteur commune par taille. `md` = hauteur des champs (`inputStyle`, 40 px) ;
 *  `sm` = le plancher qu'avait `btnSmall` (30 px), pour ne pas faire grossir les tableaux. */
export const HAUTEUR_BOUTON: Record<TailleBouton, number> = { md: 40, sm: 30 };

const COULEURS: Record<VarianteBouton, Pick<CSSProperties, 'background' | 'color' | 'border'>> = {
  // Fond cyan-600 et non `--color-primary` (cyan-500) : blanc sur cyan-500 = 2,99:1,
  // blanc sur cyan-600 = 4,26:1. Le survol (cyan-700, 6,1:1) est posé par la feuille de `Bouton`.
  primaire: { background: 'var(--cyan-600)', color: 'var(--text-on-primary)', border: '1px solid transparent' },
  secondaire: { background: 'var(--navy-900)', color: 'var(--text-on-primary)', border: '1px solid transparent' },
  contour: { background: 'var(--surface-card)', color: 'var(--text-strong)', border: '1px solid var(--border-strong)' },
  discret: { background: 'transparent', color: 'var(--text-body)', border: '1px solid transparent' },
  danger: { background: 'var(--red-50)', color: 'var(--red-600)', border: '1px solid transparent' },
};

const TAILLES: Record<TailleBouton, Pick<CSSProperties, 'fontSize' | 'padding' | 'gap'>> = {
  md: { fontSize: 13.5, padding: '6px 16px', gap: 8 },
  sm: { fontSize: 12.5, padding: '4px 12px', gap: 6 },
};

/**
 * Le style d'un bouton. Même boîte pour toutes les variantes : la bordure est
 * toujours présente (transparente quand elle ne se voit pas), sinon un bouton
 * plein serait plus petit de 2 px qu'un bouton à contour posé à côté de lui.
 *
 * ⚠️ `border` en RACCOURCI, comme l'ancien `btnSmall` : une trentaine d'écrans
 * font `{ ...btnSmall(…), borderColor: … }`, et mêler des longues (`borderWidth`…)
 * à un raccourci d'appelant déclenche l'alerte React « conflicting property ».
 */
export function styleBouton(variante: VarianteBouton, taille: TailleBouton = 'md'): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    /* ⚠️ Dans une GRILLE (`display: grid`), un enfant s'étire sur toute la
       colonne par défaut. Un bouton « Lier un document » prenait alors toute
       la largeur et se lisait comme un séparateur de sections, pas comme un
       bouton (RQ, 08/10/2026 : « pas que là »). `justifySelf` ne touche que
       les enfants de grille : en flex et en ligne, rien ne change. Un bouton
       qui doit remplir sa cellule le dit avec `width: '100%'`. */
    justifySelf: 'start',
    boxSizing: 'border-box',
    minHeight: HAUTEUR_BOUTON[taille],
    fontFamily: 'var(--font-sans)',
    fontWeight: 600,
    lineHeight: 1.2,
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    ...TAILLES[taille],
    ...COULEURS[variante],
  };
}
